/** Period close: what is left to do, what locking would change, and the way back - a table page
 * (ADR-0212, P10).
 *
 * A title, four tiles, one filter line (the period) and **one grid: the blockers**, in the order the
 * work runs, with the lock itself as the last row. The header holds **Close period** (an Admin or an
 * Approver, who are the only roles the tab is offered to) and *Lock and history*.
 *
 * **The blast radius is shown before the act.** Locking is not a flag: a closed period refuses
 * changes that would alter it (ADR-0094), so what locking does is put in front of the person who is
 * about to do it - how many figures stop accepting changes, what already issued stays as filed, who
 * is told, and how it is undone - each from the server's own read or its own sentence. They are in
 * the drawer *Close period* opens, beside the Lock button, and nowhere the act could be reached
 * without them. Nothing is counted here that the server did not count: what a *reopen* would withdraw
 * is the server's own count (`GET /periods/{id}/reopen-preview`, B14), read by the statement the
 * reopen then runs.
 *
 * **The checklist is the server's blockers**, in the order the work runs (`periodCloseModel`), and
 * the lock is the last step. There is no step the server does not check and no percentage.
 *
 * **A failed read is not an open period.** The reconciliation and the lock each say they could not
 * be read, with a retry; neither is drawn as "nothing outstanding" or "not closed", which are the two
 * claims that let someone write into a finished year or lock over a gap.
 */

import { LockKey } from "@phosphor-icons/react";
import { Link } from "@tanstack/react-router";
import { useState } from "react";

import { useMe } from "../api/auth";
import { useReader } from "../api/profile";
import { usePeriodLock, useReopenPreview } from "../api/periodLock";
import type { PeriodLock, ReopenPreview } from "../api/periodLock";
import { useClosePeriod, useReconciliation } from "../api/queries";
import type { Reconciliation } from "../api/queries";
import { ReadFailed, WriteFailed } from "../components/Loaded";
import type { ReadState } from "../components/Loaded";
import {
  ActionButton,
  DashTiles,
  Drawer,
  EMPTY_FILTERS,
  EmptyState,
  FilterLine,
  GridTable,
  HowDrawer,
  InfoPopover,
  KeyFacts,
  StatusChip,
  TablePage,
  Tile,
  TileStrip,
} from "../components/ui";
import type { ChipTone, FilterState, GridColumn } from "../components/ui";
import { instantDate, quantity, shortDate } from "../lib/format";
import { RequireSession } from "../shell/RequireSession";
import { useGridSort } from "./gridSort";
import { NoPeriod } from "./NoPeriod";
import { PeriodLockState } from "./PeriodLock";
import styles from "./PeriodClose.module.css";
import { closeSteps, figuresLine, isReconciliation, orderPeriods, periodName } from "./periodCloseModel";
import type { CloseStep } from "./periodCloseModel";
import { ScreenFrame } from "./ScreenFrame";
import { keepSearch } from "./search";
import type { ScreenSearch } from "./search";
import shared from "./Screen.module.css";
import { useScreen } from "./useScreen";

/** Its own component so the reader's preferences are read only when a closed period has a date to
 * place: a screen that read them on mount would make a request of its own on every visit. */
function ClosedOn({ at }: { at: string | null | undefined }) {
  const reader = useReader();
  return <>Closed {instantDate(at ?? null, reader)}</>;
}

const GLYPH: Record<CloseStep["state"], { word: string; tone: ChipTone }> = {
  done: { word: "Done", tone: "ok" },
  todo: { word: "To do", tone: "crit" },
  current: { word: "Next", tone: "info" },
  ahead: { word: "Waiting", tone: "neutral" },
};

/** What undoing a lock withdraws, in the server's own count (B14). Before the preview existed this
 * was told in words because the count was returned only after the act. */
function undoingIt(preview: ReadState<ReopenPreview>): string {
  const lead = "An Admin reopens it with a written reason.";
  const tail = "The period must be reconciled and closed again.";
  if (preview.data) {
    const n = preview.data.withdraws_approvals;
    const standing =
      n === 0
        ? "No approval of its filings stands now, so none would be withdrawn."
        : n === 1
          ? "1 approval of its filings stands now and would be withdrawn."
          : `${n} approvals of its filings stand now and would be withdrawn.`;
    return `${lead} ${standing} ${tail}`;
  }
  if (preview.error) {
    return `${lead} How many approvals it would withdraw could not be read. ${tail}`;
  }
  return `${lead} Counting the approvals it would withdraw…`;
}

function Radius({
  rec,
  issued,
  lockUnread,
  preview,
}: {
  rec: Reconciliation;
  issued: number | undefined;
  lockUnread: boolean;
  preview: ReadState<ReopenPreview>;
}) {
  return (
    <div data-testid="close-radius">
      <KeyFacts
        as="h3"
        layout="inline"
        facts={[
          {
            label: "Stops accepting changes",
            value: `${figuresLine(rec, (n) => quantity(String(n)))} in ${rec.label}`,
          },
          {
            label: "Stays as filed",
            value:
              issued === undefined
                ? lockUnread
                  ? "Not read"
                  : "Reading"
                : `${issued} issued ${issued === 1 ? "filing" : "filings"}, never altered or reissued`,
          },
          {
            label: "Who is told",
            value: "The other Admins and Approvers, by a notice on screen. Not the person who closes.",
          },
          {
            label: "Undoing it",
            value: undoingIt(preview),
          },
        ]}
      />
      {/* A failed count is said as a failed read with a way to retry, and the fact above it says
          "could not be read": never "none would be withdrawn". */}
      {preview.error ? (
        <ReadFailed
          of="What a reopen would withdraw"
          error={preview.error}
          onRetry={() => void preview.refetch()}
        />
      ) : null}
      <p className={styles.sentence} data-testid="close-sentence">
        {rec.locking_is_not_built}
      </p>
    </div>
  );
}

function LockDrawerBody({
  periodId,
  rec,
  lock,
  onLocked,
  onReload,
}: {
  periodId: string | undefined;
  rec: Reconciliation;
  lock: ReadState<PeriodLock>;
  onLocked: () => void;
  onReload: () => void;
}) {
  const preview = useReopenPreview(periodId);
  const close = useClosePeriod(periodId);
  const [refusal, setRefusal] = useState<unknown>(null);
  return (
    <>
      <Radius
        rec={rec}
        issued={lock.data?.issued_artifacts}
        lockUnread={Boolean(lock.error)}
        preview={preview}
      />
      <ActionButton
        variant="primary"
        data-testid="close-lock"
        disabled={!rec.can_close || close.isPending}
        reason={rec.can_close ? undefined : "Closing is unavailable until the steps marked To do are cleared."}
        onClick={() => {
          setRefusal(null);
          close.mutate(
            { expectedClosureSeq: lock.data?.closure_seq },
            {
              onSuccess: onLocked,
              onError: (error: unknown) => setRefusal(error),
            },
          );
        }}
      >
        {close.isPending ? "Closing…" : "Close period"}
      </ActionButton>
      <WriteFailed
        error={refusal}
        of="this period's blockers"
        onReload={() => {
          onReload();
          setRefusal(null);
        }}
      />
    </>
  );
}

function PeriodCloseBody() {
  const screen = useScreen();
  const { data: me } = useMe();
  const canClose = me?.role === "admin" || me?.role === "approver";
  const periods = orderPeriods(screen.organization?.periods ?? [], screen.window);
  const [line, setLine] = useState<FilterState>(EMPTY_FILTERS);
  const chosen = (line.values.period ?? [])[0];
  const period = periods.find((entry) => entry.id === chosen) ?? periods[0];
  const periodId = period?.id;
  const [lockOpen, setLockOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  const rec = useReconciliation(periodId);
  const lock = usePeriodLock(periodId);

  const reconciliation = isReconciliation(rec.data) ? rec.data : undefined;
  const steps = reconciliation ? closeSteps(reconciliation) : [];
  const closed = reconciliation?.status === "locked";

  const needle = line.search.trim().toLowerCase();
  const rows = steps.filter(
    (step) => !needle || `${step.label} ${step.detail} ${step.remedy ?? ""}`.toLowerCase().includes(needle),
  );
  const { sorted, sort, setSort } = useGridSort(rows, {
    state: (r) => GLYPH[r.state].word,
  });

  const columns: GridColumn<CloseStep>[] = [
    // Column budgets (G-Wcontrib-16, G-Wapprover-08): the blocker's sentence and its remedy are the point of the
    // table, so "What we found" takes the room that is left and wraps; it was cut mid-sentence.
    { id: "step", header: "Step", rowHeader: true, width: "240px", wrap: true, cell: (step) => step.label },
    {
      id: "state",
      header: "State",
      sortable: true,
      width: "104px",
      cell: (step) => (
        <StatusChip tone={GLYPH[step.state].tone} size="sm">
          {GLYPH[step.state].word}
        </StatusChip>
      ),
    },
    {
      id: "detail",
      header: "What we found",
      minWidth: "260px",
      wrap: true,
      cell: (step) => (
        <span>
          {step.detail}
          {step.state === "todo" && step.remedy ? ` ${step.remedy}` : ""}
        </span>
      ),
    },
    {
      id: "fix",
      header: "Fix",
      width: "200px",
      wrap: true,
      cell: (step) =>
        step.state !== "todo" ? null : step.recordId ? (
          <Link
            to="/records/$recordId"
            params={{ recordId: step.recordId }}
            search={(prev: Partial<ScreenSearch>) => ({ ...keepSearch(prev), tab: "workflow" })}
            className={shared.fixLink}
            data-testid="close-step-link"
          >
            {step.count === 1 ? "Open the approval" : "Open the longest-waiting approval"}
          </Link>
        ) : step.to ? (
          <Link to={step.to.to} search={keepSearch} className={shared.fixLink} data-testid="close-step-link">
            {step.to.label}
          </Link>
        ) : null,
    },
  ];

  const readFailed = periods.length > 0 && !rec.isPending && (rec.error !== null || !reconciliation);
  const lockedCount = periods.filter((entry) => entry.status === "locked").length;
  const todo = steps.filter((step) => step.state === "todo").length;

  const periodOptions = periods.map((entry) => ({
    value: entry.id,
    label: `${periodName(entry)}${
      entry.status === "locked" ? ` (closed ${shortDate(entry.closed_at ?? null)})` : ""
    }`,
  }));
  // The drawer is titled with the period the filter line shows as chosen: the same regime and year, said
  // the same way, so the page and the act agree on which period is meant (G-Wadmin-17).
  const chosenLabel = period ? periodName(period) : "";

  return (
    <ScreenFrame screen={screen} title="Period close" crumb="what is left, what closing changes" bodyScrolls>
      <TablePage
        icon={LockKey}
        title="Approvals & audit"
        label="Period close"
        actions={
          <>
            {closed ? (
              <StatusChip tone="ok" size="sm">
                <ClosedOn at={period?.closed_at} />
              </StatusChip>
            ) : null}
            {periodId ? (
              <ActionButton data-testid="close-history-open" onClick={() => setHistoryOpen(true)}>
                History
              </ActionButton>
            ) : null}
            {canClose && reconciliation && !closed ? (
              <ActionButton
                variant="primary"
                data-testid="close-open"
                onClick={() => setLockOpen(true)}
              >
                Close period
              </ActionButton>
            ) : null}
            <InfoPopover infoKey="audit.close" />
          </>
        }
        tiles={
          reconciliation ? (
            <TileStrip label="Where the period stands">
              <Tile value={String(todo)} label="Blockers" testId="close-blockers" />
              <Tile
                value={String(lockedCount)}
                unit={`of ${String(periods.length)}`}
                label="Periods closed"
                testId="close-locked"
              />
              <Tile value={quantity(String(reconciliation.non_market_figures))} label="Figures in period" testId="close-figures" />
              <Tile
                value={lock.data ? String(lock.data.issued_artifacts) : "-"}
                label="Issued filings"
                loading={lock.isPending}
                testId="close-issued"
              />
            </TileStrip>
          ) : periods.length === 0 ? (
            <DashTiles
              labels={["Blockers", "Periods closed", "Figures in period", "Issued filings"]}
              label="Where the period stands"
            />
          ) : undefined
        }
        filters={
          periods.length === 0 ? undefined : (
            <FilterLine
              filters={[
                {
                  id: "period",
                  label: "Period",
                  mode: "single",
                  allLabel: periodOptions[0]?.label ?? "All",
                  options: periodOptions,
                },
              ]}
              state={line}
              onChange={setLine}
              resultCount={reconciliation ? { shown: rows.length, total: steps.length, noun: "steps" } : undefined}
              actions={<HowDrawer infoKey="audit.close-how" label="How closing works" />}
            />
          )
        }
        table={
          periods.length === 0 || readFailed ? undefined : (
            <>
              {/* The lock is read for the issued-filings tile and for the act; a read that failed is said,
                  and the tile shows a dash and never a zero. */}
              {lock.error ? (
                <ReadFailed
                  of="This period's close"
                  error={lock.error}
                  onRetry={() => void lock.refetch()}
                />
              ) : null}
              {!canClose ? (
                <p className={shared.note} data-testid="close-who-can">
                  An Admin or an Approver closes a period.
                </p>
              ) : null}
              <GridTable
                caption="What is left before the period can be closed"
                columns={columns}
                rows={sorted}
                rowKey={(step) => step.key}
                rowAttributes={(step) => ({
                  "data-testid": "close-step",
                  "data-state": step.state,
                  "data-step": step.key,
                })}
                loading={rec.isPending}
                sort={sort}
                onSortChange={setSort}
                empty={<EmptyState compact title="No step matches the search." testId="close-empty" />}
                testId="close-steps"
              />
            </>
          )
        }
        state={
          periods.length === 0 ? (
            <div data-testid="close-no-periods">
              <NoPeriod />
            </div>
          ) : readFailed ? (
            <ReadFailed
              of="This period's reconciliation"
              error={rec.error ?? new Error("The server did not answer for this period.")}
              onRetry={() => void rec.refetch()}
            />
          ) : undefined
        }
      />

      {/* What locking does, and the act. The Lock button is last in the drawer and only a role that
          may lock is ever offered the drawer. Its reads (what a reopen would withdraw) are made when it
          opens and not before. */}
      <Drawer
        open={lockOpen && reconciliation !== undefined && !closed}
        onClose={() => setLockOpen(false)}
        title={reconciliation ? `Close period: ${chosenLabel}` : "Close period"}
        width="md"
        testId="close-drawer"
      >
        {reconciliation ? (
          <LockDrawerBody
            periodId={periodId}
            rec={reconciliation}
            lock={lock}
            onLocked={() => setLockOpen(false)}
            onReload={() => {
              void rec.refetch();
              void lock.refetch();
            }}
          />
        ) : null}
      </Drawer>

      <Drawer
        open={historyOpen && periodId !== undefined}
        onClose={() => setHistoryOpen(false)}
        title="History"
        width="md"
        testId="close-history-drawer"
      >
        <PeriodLockState periodId={periodId} onReload={() => void rec.refetch()} />
      </Drawer>
    </ScreenFrame>
  );
}

export function PeriodCloseScreen() {
  return (
    <RequireSession>
      <PeriodCloseBody />
    </RequireSession>
  );
}
